import { Award, Hammer, Home, Users, Sparkles, MapPin } from "lucide-react";
import { Reveal } from "./Reveal";

const milestones = [
  {
    year: "1984",
    title: "The Crew is founded",
    body: "Bart Baldwin picks up a brush in Arlington, TX and starts a business built on one rule: show up every day and do it right.",
    Icon: Sparkles,
  },
  {
    year: "1992",
    title: "Expanding into siding & wood replacement",
    body: "After years of exterior painting, we add full siding and rotted-wood replacement so homeowners get one trusted crew for the whole job.",
    Icon: Hammer,
  },
  {
    year: "2001",
    title: "1,000+ Arlington homes painted",
    body: "Word of mouth turns into a reputation. Neighbors recommend neighbors, and we cross the thousand-home mark — still owner-operated.",
    Icon: Home,
  },
  {
    year: "2010",
    title: "Second-generation customers",
    body: "Children of our earliest clients call us for their own homes. Repeat families become the heart of the business.",
    Icon: Users,
  },
  {
    year: "2018",
    title: "Full interior services added",
    body: "Drywall repair, texture matching, and interior trim replacement round out the offering — inside and out, top to bottom.",
    Icon: Award,
  },
  {
    year: "2024",
    title: "40 years strong, still local",
    body: "Four decades in, still based in Arlington, still answering the phone ourselves. Same standards, same handshake.",
    Icon: MapPin,
  },
];

export function Timeline() {
  return (
    <section className="relative overflow-hidden bg-primary text-primary-foreground">
      <div className="absolute inset-0 -z-0 bg-[radial-gradient(circle_at_20%_10%,_oklch(0.58_0.14_55/0.18),_transparent_55%)]" />
      <div className="absolute inset-0 -z-0 bg-[radial-gradient(circle_at_80%_90%,_oklch(0.58_0.14_55/0.15),_transparent_55%)]" />

      <div className="relative mx-auto max-w-7xl px-6 py-28">
        <Reveal className="max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-accent">
            Our Story
          </p>
          <h2 className="mt-3 font-display text-4xl leading-tight md:text-6xl">
            Four decades of{" "}
            <span className="text-accent">Arlington craftsmanship.</span>
          </h2>
          <p className="mt-6 max-w-xl text-lg text-primary-foreground/75">
            Since 1984, The Crew has been protecting and beautifying homes across North Texas — one
            honest project at a time.
          </p>
        </Reveal>

        <div className="relative mt-20">
          <div className="absolute left-6 top-0 bottom-0 w-px bg-gradient-to-b from-accent via-accent/40 to-primary-foreground/15 md:left-1/2 md:-translate-x-1/2" />

          <ul className="space-y-16 md:space-y-24">
            {milestones.map((m, i) => {
              const isRight = i % 2 === 1;
              return (
                <li key={m.year} className="relative">
                  <div
                    className={`grid items-center gap-8 md:grid-cols-2 ${
                      isRight ? "md:[&>*:first-child]:order-2" : ""
                    }`}
                  >
                    <Reveal
                      className={`pl-16 md:pl-0 ${isRight ? "md:pl-12" : "md:pr-12 md:text-right"}`}
                    >
                      <div
                        className={`inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-accent ${
                          isRight ? "" : "md:flex-row-reverse"
                        }`}
                      >
                        <m.Icon className="h-3.5 w-3.5" />
                        {m.year}
                      </div>
                      <h3 className="mt-4 font-display text-2xl md:text-3xl">{m.title}</h3>
                      <p className="mt-3 text-primary-foreground/75 leading-relaxed">{m.body}</p>
                    </Reveal>
                    <div className="hidden md:block" />
                  </div>

                  <div className="absolute left-6 top-2 -translate-x-1/2 md:left-1/2">
                    <div className="relative flex h-5 w-5 items-center justify-center">
                      <span className="absolute inset-0 animate-ping rounded-full bg-accent/40" />
                      <span className="relative h-3 w-3 rounded-full bg-accent ring-4 ring-primary" />
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <Reveal className="mt-24 grid gap-8 border-t border-primary-foreground/15 pt-12 md:grid-cols-3">
          {[
            { stat: "40+", label: "Years in business" },
            { stat: "1984", label: "Founded in Arlington, TX" },
            { stat: "100%", label: "Owner-operated by Bart" },
          ].map((s) => (
            <div key={s.label}>
              <p className="font-display text-5xl text-accent md:text-6xl">{s.stat}</p>
              <p className="mt-2 text-sm uppercase tracking-widest text-primary-foreground/70">
                {s.label}
              </p>
            </div>
          ))}
        </Reveal>
      </div>
    </section>
  );
}
